import { customerDetailPrePaths } from '../../utils/constants';

export interface BasicPath<F extends string> {
  tag: 'basic';
  path: F;
}

export interface SingleParamPath<F extends string, S extends string = ''> {
  tag: 'singleParam';
  paramName: string;
  prePath: F;
  postPath: S;
}

export interface DoubleParamPath<F extends string, S extends string, T extends string> {
  tag: 'doubleParam';
  firstParamName: string;
  secondParamName: string;
  prePath: F;
  middlePath: S;
  postPath: T;
}

export type PathStructure<F extends string, S extends string = '', T extends string = ''> = BasicPath<F> | SingleParamPath<F, S> | DoubleParamPath<F, S, T>;

const getRouterPath = (p: PathStructure<string, string, string>): string => {
  switch (p.tag) {
    case 'basic': {
      return p.path;
    }
    case 'singleParam': {
      return `${p.prePath}/:${p.paramName}${p.postPath}`;
    }
    case 'doubleParam': {
      return `${p.prePath}/:${p.firstParamName}${p.middlePath}/:${p.secondParamName}${p.postPath}`;
    }
  }
};

export const routerPaths = <R extends Record<string, PathStructure<string, string, string>>>(r: R) => {
  const entries = Object.entries(r).map(([key, path]) => {
    return [key, { ...path, routerPath: getRouterPath(path) }];
  });

  return Object.fromEntries(entries) as {
    [K in keyof R]: { routerPath: string } & (R[K] extends BasicPath<infer FF>
      ? BasicPath<FF>
      : R[K] extends SingleParamPath<infer FF, infer SS>
        ? SingleParamPath<FF, SS>
        : R[K] extends DoubleParamPath<infer FF, infer SS, infer TT>
          ? DoubleParamPath<FF, SS, TT>
          : never);
  };
};

export const paths = routerPaths({
  404: { tag: 'basic', path: '/404' },
  home: { tag: 'basic', path: '/' },
  login: { tag: 'basic', path: '/login' },
  lookup: { tag: 'basic', path: '/lookup' },
  lookups: { tag: 'basic', path: '/lookups' },

  analytics: { tag: 'basic', path: '/analytics' },
  insights: { tag: 'basic', path: '/insights' },
  import: { tag: 'basic', path: '/import' },
  identityGraph: { tag: 'basic', path: '/identity-graph' },
  export: { tag: 'basic', path: '/export' },
  ruleEngine: { tag: 'basic', path: '/rule-engine' },
  customPricing: { tag: 'basic', path: '/custom-pricing' },
  settings: { tag: 'basic', path: '/settings' },
  overviewDigitalScore: { tag: 'basic', path: '/stats/digital-score-overview' },
  overviewPhone: { tag: 'basic', path: '/stats/phone-overview' },
  overviewEmail: { tag: 'basic', path: '/stats/email-overview' },
  overviewIp: { tag: 'basic', path: '/stats/ip-address-overview' },
  featureDetails: { tag: 'basic', path: '/feature-details' },
  // old path for backward compatibility
  customerDetails: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.oldCustomer,
    postPath: '',
  },
  customerPhoneDetails: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.oldCustomerPhone,
    postPath: '',
  },
  customerEmailDetails: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.oldCustomerEmail,
    postPath: '',
  },

  customerIpAddressDetails: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.oldCustomerIpAddress,
    postPath: '',
  },
  // end
  customerDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customer,
    postPath: '',
  },

  customerPhoneDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerPhone,
    postPath: '',
  },

  customerEmailDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerEmail,
    postPath: '',
  },

  customerIpDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerIpAddress,
    postPath: '',
  },

  customerDomainDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerDomain,
    postPath: '',
  },

  customerLoginDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerLogin,
    postPath: '',
  },

  customerLoginEnrollmentDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerLoginEnrollment,
    postPath: '',
  },

  customerBotDetectionDetailPage: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: customerDetailPrePaths.customerBotDetection,
    postPath: '',
  },

  widgets: { tag: 'basic', path: '/management/widgets' },
  appKeys: { tag: 'basic', path: '/management/app-keys' },
  billing: { tag: 'basic', path: '/management/billing' },
  companies: { tag: 'basic', path: '/admin/companies' },
  users: { tag: 'basic', path: '/admin/users' },
  apps: { tag: 'basic', path: '/admin/apps' },
  utilities: { tag: 'basic', path: '/admin/utilities' },
  adminDashboard: { tag: 'basic', path: '/admin/dashboard' },
  userDetail: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: '/admin/user',
    postPath: '',
  },
  companyDetail: {
    tag: 'singleParam',
    paramName: 'id',
    prePath: '/admin/company',
    postPath: '',
  },
  completeUser: {
    tag: 'basic',
    path: '/complete',
  },
  loading: {
    tag: 'basic',
    path: '/loading',
  },
  signUp: {
    tag: 'basic',
    path: '/auth/sign-up',
  },
  forgotPassword: {
    tag: 'basic',
    path: '/auth/forgot-password',
  },
  verificationCode: {
    tag: 'basic',
    path: '/auth/verification-code',
  },
  registrationComplete: {
    tag: 'basic',
    path: '/auth/thanks',
  },

  forgotPasswordSent: {
    tag: 'basic',
    path: '/auth/forgot-password/sent',
  },
  passwordExpired: {
    tag: 'basic',
    path: '/auth/password-expired',
  },
  authError: {
    tag: 'basic',
    path: '/error',
  },
  confirmSuccess: {
    tag: 'basic',
    path: '/confirm-success',
  },
} as const);
